import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../../config/api';
import { authHeaders } from '../../services/auth';
import DataTable from '../../components/ui/DataTable';
import Spinner from '../../components/ui/Spinner';
import ToastProvider, { useToast } from '../../components/ui/ToastProvider';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Modal from '../../components/ui/Modal';
import ImageUpload from '../../components/admin/ImageUpload';
import { adminTheme } from '../../theme-admin';
import { cache } from '../../utils/cache';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${adminTheme.spacing.xl};
  animation: slideIn 0.3s ease-out;
`;

const Title = styled.h2`
  font-family: ${adminTheme.fonts.heading};
  font-size: ${adminTheme.fontSizes['3xl']};
  font-weight: ${adminTheme.fontWeights.bold};
  color: ${adminTheme.colors.gray[900]};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${adminTheme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${adminTheme.fontSizes.sm};
  font-weight: ${adminTheme.fontWeights.semibold};
  color: ${adminTheme.colors.gray[700]};
`;

const Input = styled.input`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  transition: ${adminTheme.transitions.fast};
  font-family: ${adminTheme.fonts.primary};
  color: ${adminTheme.colors.gray[900]};
  background-color: ${adminTheme.colors.background.primary};

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }

  &::placeholder {
    color: ${adminTheme.colors.gray[400]};
  }
`;

const TableWrapper = styled.div`
  background: ${adminTheme.colors.background.primary};
  border: 1px solid ${adminTheme.colors.gray[200]};
  border-radius: ${adminTheme.borderRadius['2xl']};
  padding: ${adminTheme.spacing.xl};
  box-shadow: ${adminTheme.shadows.md};
  animation: slideUp 0.4s ease-out;
`;

const ActionButton = styled.button`
  padding: ${adminTheme.spacing.sm} ${adminTheme.spacing.md};
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.semibold};
  font-size: ${adminTheme.fontSizes.sm};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  border: 1px solid ${adminTheme.colors.gray[300]};
  background: ${adminTheme.colors.background.primary};
  color: ${adminTheme.colors.gray[700]};

  &:hover {
    background: ${adminTheme.colors.gray[100]};
    transform: translateY(-1px);
  }
`;

const DeleteButton = styled(ActionButton)`
  border-color: ${adminTheme.colors.danger[300]};
  background: ${adminTheme.colors.danger[500]};
  color: white;

  &:hover {
    background: ${adminTheme.colors.danger[600]};
  }
`;

const ModalForm = styled.div`
  display: grid;
  gap: ${adminTheme.spacing.lg};
`;

const TextArea = styled.textarea`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  transition: ${adminTheme.transitions.fast};
  font-family: ${adminTheme.fonts.primary};
  min-height: 100px;
  resize: vertical;
  color: ${adminTheme.colors.gray[900]};
  background-color: ${adminTheme.colors.background.primary};

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }

  &::placeholder {
    color: ${adminTheme.colors.gray[400]};
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${adminTheme.spacing.sm};
  align-items: center;
`;

const TBButton = styled.button`
  padding: ${adminTheme.spacing.xs} ${adminTheme.spacing.sm};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.md};
  background: ${adminTheme.colors.background.primary};
  color: ${adminTheme.colors.gray[800]};
  font-size: ${adminTheme.fontSizes.sm};
  cursor: pointer;
  &:hover { background: ${adminTheme.colors.gray[100]}; }
`;

const Select = styled.select`
  padding: ${adminTheme.spacing.md};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.base};
  transition: ${adminTheme.transitions.fast};
  font-family: ${adminTheme.fonts.primary};
  cursor: pointer;
  background: white;
  color: ${adminTheme.colors.gray[900]};

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }
`;

const TagInput = styled.div`
  display: flex;
  gap: ${adminTheme.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
  padding: ${adminTheme.spacing.sm};
  border: 1px solid ${adminTheme.colors.gray[300]};
  border-radius: ${adminTheme.borderRadius.lg};
  min-height: 45px;
  background: white;

  &:focus-within {
    border-color: ${adminTheme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${adminTheme.colors.primary[100]};
  }
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${adminTheme.spacing.xs};
  padding: ${adminTheme.spacing.xs} ${adminTheme.spacing.sm};
  background: ${adminTheme.colors.primary[100]};
  color: ${adminTheme.colors.primary[700]};
  border-radius: ${adminTheme.borderRadius.md};
  font-size: ${adminTheme.fontSizes.sm};
  font-weight: ${adminTheme.fontWeights.medium};
  
  button {
    border: none;
    background: none;
    color: ${adminTheme.colors.primary[600]};
    cursor: pointer;
    padding: 0;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    
    &:hover {
      background: ${adminTheme.colors.primary[200]};
      color: ${adminTheme.colors.primary[800]};
    }
  }
`;

const TagInputField = styled.input`
  border: none;
  outline: none;
  flex: 1;
  min-width: 120px;
  padding: ${adminTheme.spacing.xs};
  font-size: ${adminTheme.fontSizes.base};
  font-family: ${adminTheme.fonts.primary};
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.sm};
  
  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }
`;

const CharCount = styled.small`
  color: ${props => props.isOver ? adminTheme.colors.danger[500] : adminTheme.colors.gray[600]};
  font-size: ${adminTheme.fontSizes.xs};
  margin-top: -${adminTheme.spacing.xs};
`;

const CreateButton = styled.button`
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.xl};
  background: ${adminTheme.gradients.purple};
  color: white;
  border: none;
  border-radius: ${adminTheme.borderRadius.lg};
  font-weight: ${adminTheme.fontWeights.bold};
  font-size: ${adminTheme.fontSizes.base};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  box-shadow: ${adminTheme.shadows.md};
  display: flex;
  align-items: center;
  gap: ${adminTheme.spacing.sm};
  white-space: nowrap;

  &:hover {
    filter: brightness(1.1);
    transform: translateY(-2px);
    box-shadow: ${adminTheme.shadows.lg};
  }

  &:active {
    transform: translateY(0);
  }
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${adminTheme.spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ThreeColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${adminTheme.spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

function PageBody() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [editMode, setEditMode] = useState('markdown'); // 'markdown' | 'wysiwyg'
  const [contentEl, setContentEl] = useState(null);
  const toast = useToast();

  const getInitialForm = () => ({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'General',
    tags: [],
    readTime: 5,
    author: {
      name: '',
      bio: '',
      avatar: ''
    },
    imageUrl: '',
    featured: false,
    published: false
  });

  const [editForm, setEditForm] = useState(getInitialForm());
  const [showPreview, setShowPreview] = useState(true);

  const isHtmlContent = (s = '') => /<\/?[a-z][\s\S]*>/i.test(s);

  const load = useCallback(async (forceRefresh = false) => {
    // Check cache first
    if (!forceRefresh) {
      const cached = cache.get('blogs-list-admin');
      if (cached) {
        setItems(cached);
        return;
      }
    }

    setLoading(true);
    try {
      // Include content for admin panel editing
      const res = await fetch(`${API_BASE_URL}/blogs?includeContent=true&includeAll=true`);
      const data = await res.json();
      const blogs = data.data?.blogs || [];
      setItems(blogs);
      // Cache for 5 minutes
      cache.set('blogs-list-admin', blogs);
    } catch (error) {
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
    // toast is stable from react-toastify
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setIsCreating(true);
    setEditing(null);
    setEditForm(getInitialForm());
    setEditMode('markdown');
  };

  const openEdit = (blog) => {
    setIsCreating(false);
    setEditing(blog);
    setEditForm({
      title: blog.title || '',
      slug: blog.slug || '',
      excerpt: blog.excerpt || '',
      content: blog.content || '',
      category: blog.category || 'General',
      tags: blog.tags || [],
      readTime: typeof blog.readTime === 'string' ? parseInt(blog.readTime) || 5 : blog.readTime || 5,
      author: {
        name: blog.author?.name || '',
        bio: blog.author?.bio || '',
        avatar: blog.author?.avatar || ''
      },
      imageUrl: blog.image || blog.imageUrl || '',
      featured: blog.featured || false,
      published: blog.isPublished !== undefined ? blog.isPublished : (blog.published || false)
    });
    setEditMode(isHtmlContent(blog.content || '') ? 'wysiwyg' : 'markdown');
  };

  const closeModal = () => {
    setIsCreating(false);
    setEditing(null);
    setEditForm(getInitialForm());
    setTagInput('');
  };

  const saveBlog = async () => {
    try {
      const url = isCreating
        ? `${API_BASE_URL}/blogs`
        : `${API_BASE_URL}/blogs/${editing._id}`;
      
      const method = isCreating ? 'POST' : 'PUT';

      // Map frontend fields to backend field names
      const blogData = {
        ...editForm,
        image: editForm.imageUrl,
        isPublished: editForm.published,
        readTime: typeof editForm.readTime === 'number' ? `${editForm.readTime} min read` : editForm.readTime
      };
      
      // Remove frontend-only fields
      delete blogData.imageUrl;
      delete blogData.published;

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify(blogData)
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Operation failed');
      }

      toast.success(isCreating ? 'Blog created! 🎉' : 'Blog updated! ✨');
      closeModal();
      cache.invalidate('blogs-list-admin');
      load(true);
    } catch (error) {
      toast.error(error.message || 'Failed to save blog');
    }
  };

  // (Deprecated) Markdown helper utilities removed in favor of WYSIWYG editor

  // Utility: slugify a title
  const slugify = (s = '') =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .slice(0, 120);

  // Auto-calc read time from content (~200 wpm)
  const computeReadTime = (content = '') => {
    // Strip HTML tags if present for accurate word count
    const text = content.replace(/<[^>]+>/g, ' ');
    const words = (text.match(/\S+/g) || []).length;
    return Math.max(2, Math.ceil(words / 200));
  };

  // --- Markdown-like preview renderer (matches BlogDetail rules) ---------
  const renderPreviewHtml = (content) => {
    if (!content) return '';
    const lines = content.split('\n');
    let html = ''; let inList = false; let inCode = false;

    const closeList = () => { if (inList) { html += '</ul>'; inList = false; } };
    const esc = (s) => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

    for (let raw of lines) {
      const line = raw.trimEnd();
      if (line.startsWith('```')) { if (!inCode) { closeList(); html += '<pre><code>'; inCode = true; } else { html += '</code></pre>'; inCode = false; } continue; }
      if (inCode) { html += `${esc(raw)}\n`; continue; }
      if (line.startsWith('![') && line.includes('](')) {
        closeList();
        const m = line.match(/!\[(.*?)\]\((.*?)(?:\s+"(.*?)")?\)/);
        if (m) { const [,alt,url,cap] = m; html += cap ? `<figure><img src="${url}" alt="${alt}" /><figcaption>${cap}</figcaption></figure>` : `<img src="${url}" alt="${alt}" />`; }
        continue;
      }
      const head = (lvl, text) => { const id = text.toLowerCase().replace(/[^a-z0-9\s-]/g,'').trim().replace(/\s+/g,'-'); return `<h${lvl} id="${id}">${text}</h${lvl}>`; };
      if (line.startsWith('# ')) { closeList(); html += head(1, line.slice(2)); continue; }
      if (line.startsWith('## ')) { closeList(); html += head(2, line.slice(3)); continue; }
      if (line.startsWith('### ')) { closeList(); html += head(3, line.slice(4)); continue; }
      if (line.startsWith('> ')) { closeList(); html += `<blockquote>${line.slice(2)}</blockquote>`; continue; }
      if (line.startsWith('- ')) { if (!inList) { html += '<ul>'; inList = true; } html += `<li>${line.slice(2)}</li>`; continue; }
      if (line.trim() === '') { closeList(); continue; }
      closeList(); html += `<p>${line}</p>`;
    }
    closeList(); if (inCode) html += '</code></pre>';
    return html;
  };

  const PreviewShell = styled.div`
    border: 1px solid ${adminTheme.colors.gray[200]};
    border-radius: ${adminTheme.borderRadius.xl};
    padding: ${adminTheme.spacing.xl};
    background: ${adminTheme.colors.background.primary};
    box-shadow: ${adminTheme.shadows.sm};
    max-height: 60vh; overflow: auto;

    h1, h2, h3 { margin: 1rem 0 .5rem; font-family: ${adminTheme.fonts.heading}; }
    p { margin: 0 0 .5rem; line-height: 1.7; color: ${adminTheme.colors.gray[700]}; }
    ul, ol { margin: .5rem 0 1rem; padding-left: 1.25rem; }
    img { max-width: 100%; height: auto; border-radius: 12px; box-shadow: ${adminTheme.shadows.sm}; margin: 1rem 0; }
    blockquote { border-left: 4px solid ${adminTheme.colors.primary[500]}; background: ${adminTheme.colors.gray[100]}; padding: .75rem 1rem; border-radius: 8px; color: ${adminTheme.colors.gray[700]}; }
    figcaption { text-align:center; font-size: 12px; color: ${adminTheme.colors.gray[500]}; margin-top: .25rem; }
  `;

  // Quill modules and formats (WYSIWYG)
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'code-block'],
      ['link', 'image'],
      [{ align: [] }],
      ['clean']
    ]
  }), []);

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'blockquote', 'code-block',
    'link', 'image', 'align'
  ];

  // Table row actions
  const onDelete = async (id) => {
    setConfirmId(id);
  };

  const confirmDelete = async () => {
    const id = confirmId;
    setConfirmId(null);
    try {
      const res = await fetch(`${API_BASE_URL}/blogs/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) throw new Error('delete failed');
      toast.success('Blog deleted');
      cache.invalidate('blogs-list-admin');
      load(true);
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  // Tags helpers
  const addTag = () => {
    if (tagInput.trim() && !editForm.tags.includes(tagInput.trim())) {
      setEditForm({
        ...editForm,
        tags: [...editForm.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setEditForm({
      ...editForm,
      tags: editForm.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && tagInput === '' && editForm.tags.length > 0) {
      removeTag(editForm.tags[editForm.tags.length - 1]);
    }
  };

  // Markdown helpers (used only when editMode === 'markdown')
  const insertAtCursor = (snippet) => {
    if (!contentEl) {
      setEditForm({ ...editForm, content: (editForm.content || '') + snippet });
      return;
    }
    const el = contentEl;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const before = editForm.content.slice(0, start);
    const after = editForm.content.slice(end);
    const next = `${before}${snippet}${after}`;
    setEditForm({ ...editForm, content: next });
    setTimeout(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const insertTemplate = () => insertAtCursor(
`# Intro title

Short opening paragraph that explains the article.

![Cover image](https://your.cdn/image.jpg "An optional caption")

## Section one

- Bullet point one
- Bullet point two

> Key takeaway quote or callout.

### Sub section

Regular paragraph text continues here. Link example: https://example.com

![Diagram left](https://your.cdn/diagram.jpg "Diagram caption left")

## Conclusion

Final summary and next steps.
`
  );

  const snippets = {
    h2: `\n\n## Section title\n\n`,
    h3: `\n\n### Sub section\n\n`,
    list: `\n- First item\n- Second item\n- Third item\n\n`,
    quote: `\n> Insightful quote or note.\n\n`,
    code: `\n\n\\n\`\`\`js\n// code here\nconsole.log('hello');\n\`\`\`\n\n`,
    image: `\n![Alt text](https://your.cdn/image.jpg "Optional caption")\n\n`,
  };

  // Render
  return (
    <div>
      <PageHeader>
        <Title>
          <span>📝</span>
          Blog Management
        </Title>
        <CreateButton onClick={openCreate}>
          <span>➕</span>
          Create New Blog
        </CreateButton>
      </PageHeader>

      <TableWrapper>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spinner label="Loading blogs..." />
          </div>
        ) : (
          <DataTable
            columns={[
              { key: 'title', header: 'Title', width: '2fr' },
              { key: 'category', header: 'Category', width: '1fr' },
              {
                key: 'tags',
                header: 'Tags',
                width: '1.5fr',
                render: (b) => b.tags?.join(', ') || 'N/A'
              },
              {
                key: 'published',
                header: 'Status',
                width: '0.8fr',
                render: (b) => (
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    background: b.published ? '#10b981' : '#6b7280',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {b.published ? '✓ Published' : '○ Draft'}
                  </span>
                )
              },
            ]}
            rows={items}
            actions={(b) => (
              <>
                <ActionButton onClick={() => openEdit(b)}>✏️ Edit</ActionButton>
                <DeleteButton onClick={() => onDelete(b._id)}>🗑️ Delete</DeleteButton>
              </>
            )}
          />
        )}
      </TableWrapper>

      <ConfirmDialog
        open={!!confirmId}
        onCancel={() => setConfirmId(null)}
        onConfirm={confirmDelete}
        title="Delete blog post?"
        description="This action cannot be undone."
      />

      <Modal
        open={isCreating || !!editing}
        title={isCreating ? '✨ Create New Blog' : '✏️ Edit Blog'}
        onCancel={closeModal}
        onSave={saveBlog}
        saveLabel={isCreating ? 'Create Blog' : 'Save Changes'}
      >
        <ModalForm>
          <TwoColumn>
            <FormGroup>
              <Label>Title *</Label>
              <Input
                placeholder="10 Tips for Better Coding"
                value={editForm.title}
                onChange={(e) => {
                  const title = e.target.value;
                  const next = { ...editForm, title };
                  if (!slugTouched) {
                    next.slug = slugify(title);
                  }
                  setEditForm(next);
                }}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Slug *</Label>
              <Input
                placeholder="10-tips-better-coding"
                value={editForm.slug}
                onChange={(e) => { setSlugTouched(true); setEditForm({ ...editForm, slug: slugify(e.target.value) }); }}
                required
              />
            </FormGroup>
          </TwoColumn>

          <FormGroup>
            <Label>Excerpt * (max 500 characters)</Label>
            <TextArea
              placeholder="A brief summary of the blog post..."
              value={editForm.excerpt}
              onChange={(e) => setEditForm({ ...editForm, excerpt: e.target.value })}
              maxLength={500}
              rows={3}
              required
            />
            <CharCount isOver={editForm.excerpt.length > 500}>
              {editForm.excerpt.length}/500 characters
            </CharCount>
          </FormGroup>

          <FormGroup>
            <Label>Content *</Label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
              <small style={{ color: '#6b7280' }}>Editing mode:</small>
              <Select value={editMode} onChange={(e) => setEditMode(e.target.value)}>
                <option value="markdown">Markdown</option>
                <option value="wysiwyg">WYSIWYG</option>
              </Select>
              <div style={{ flex: 1 }} />
              <TBButton type="button" onClick={() => setShowPreview(p => !p)}>{showPreview ? 'Hide Preview' : 'Show Preview'}</TBButton>
            </div>

            {editMode === 'markdown' ? (
              <>
                <Toolbar>
                  <TBButton type="button" onClick={() => insertAtCursor(snippets.h2)}>H2</TBButton>
                  <TBButton type="button" onClick={() => insertAtCursor(snippets.h3)}>H3</TBButton>
                  <TBButton type="button" onClick={() => insertAtCursor(snippets.list)}>List</TBButton>
                  <TBButton type="button" onClick={() => insertAtCursor(snippets.quote)}>Quote</TBButton>
                  <TBButton type="button" onClick={() => insertAtCursor(snippets.code)}>Code</TBButton>
                  <TBButton type="button" onClick={() => insertAtCursor(snippets.image)}>Image</TBButton>
                  <TBButton type="button" onClick={insertTemplate}>Insert Template</TBButton>
                </Toolbar>
                <TextArea
                  placeholder="Full blog post content using simple Markdown..."
                  value={editForm.content}
                  onChange={(e) => {
                    const content = e.target.value;
                    const minutes = computeReadTime(content);
                    setEditForm({ ...editForm, content, readTime: minutes });
                  }}
                  rows={14}
                  required
                  ref={setContentEl}
                />
                <CharCount>
                  Tips: use '#', '##', '###' for headings, '- ' for lists, {'>'} for quotes, triple backticks for code, and
                  image syntax: ![alt](url "caption"). Add 'left' or 'right' in the alt text to align the image.
                </CharCount>
              </>
            ) : (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                <ReactQuill
                  theme="snow"
                  value={editForm.content}
                  onChange={(html) => {
                    const minutes = computeReadTime(html || '');
                    setEditForm({ ...editForm, content: html, readTime: minutes });
                  }}
                  modules={quillModules}
                  formats={quillFormats}
                />
              </div>
            )}

            {showPreview && (
              <div style={{ marginTop: adminTheme.spacing.lg }}>
                <Label>Live Preview</Label>
                <PreviewShell>
                  {editForm.title && <h1 style={{ marginTop: 0 }}>{editForm.title}</h1>}
                  {editForm.excerpt && <p style={{ color: adminTheme.colors.gray[600] }}>{editForm.excerpt}</p>}
                  {editForm.imageUrl && <img src={editForm.imageUrl} alt="Featured" />}
                  {editMode === 'markdown' ? (
                    <div dangerouslySetInnerHTML={{ __html: renderPreviewHtml(editForm.content) }} />
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: editForm.content || '' }} />
                  )}
                </PreviewShell>
              </div>
            )}
          </FormGroup>

          <TwoColumn>
            <FormGroup>
              <Label>Category</Label>
              <Select
                value={editForm.category}
                onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
              >
                <option value="General">General</option>
                <option value="Tutorial">Tutorial</option>
                <option value="News">News</option>
                <option value="Case Study">Case Study</option>
                <option value="Best Practices">Best Practices</option>
                <option value="Industry Insights">Industry Insights</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Read Time (minutes)</Label>
              <Input
                type="number"
                min="1"
                placeholder="5"
                value={editForm.readTime}
                onChange={(e) => setEditForm({ ...editForm, readTime: parseInt(e.target.value) || 5 })}
              />
            </FormGroup>
          </TwoColumn>

          <FormGroup>
            <Label>Tags (press Enter to add)</Label>
            <TagInput>
              {editForm.tags.map((tag, idx) => (
                <Tag key={idx}>
                  {tag}
                  <button onClick={() => removeTag(tag)}>×</button>
                </Tag>
              ))}
              <TagInputField
                placeholder="Add tag..."
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                onBlur={addTag}
              />
            </TagInput>
          </FormGroup>

          <FormGroup>
            <ImageUpload
              label="Blog Image"
              value={editForm.imageUrl}
              onChange={(url) => setEditForm({ ...editForm, imageUrl: url })}
              folder="blogs"
            />
          </FormGroup>

          <ThreeColumn>
            <FormGroup>
              <Label>Author Name</Label>
              <Input
                placeholder="John Doe"
                value={editForm.author.name}
                onChange={(e) => setEditForm({
                  ...editForm,
                  author: { ...editForm.author, name: e.target.value }
                })}
              />
            </FormGroup>

            <FormGroup>
              <Label>Author Bio</Label>
              <Input
                placeholder="Senior Developer"
                value={editForm.author.bio}
                onChange={(e) => setEditForm({
                  ...editForm,
                  author: { ...editForm.author, bio: e.target.value }
                })}
              />
            </FormGroup>

            <FormGroup>
              <Label>Author Avatar</Label>
              <Input
                placeholder="https://example.com/avatar.jpg"
                value={editForm.author.avatar}
                onChange={(e) => setEditForm({
                  ...editForm,
                  author: { ...editForm.author, avatar: e.target.value }
                })}
              />
            </FormGroup>
          </ThreeColumn>

          <TwoColumn>
            <CheckboxGroup>
              <input
                type="checkbox"
                checked={editForm.featured}
                onChange={(e) => setEditForm({ ...editForm, featured: e.target.checked })}
                id="featured-blog"
              />
              <Label htmlFor="featured-blog">⭐ Featured Blog</Label>
            </CheckboxGroup>

            <CheckboxGroup>
              <input
                type="checkbox"
                checked={editForm.published}
                onChange={(e) => setEditForm({ ...editForm, published: e.target.checked })}
                id="published-blog"
              />
              <Label htmlFor="published-blog">🌐 Published</Label>
            </CheckboxGroup>
          </TwoColumn>
        </ModalForm>
      </Modal>
    </div>
  );
}

export default function AdminBlogs() {
  return (
    <ToastProvider>
      <PageBody />
    </ToastProvider>
  );
}
